import React, { useEffect, useState } from 'react';
import { Box, Paper, Stack, Typography } from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../hooks';
import { addToCart, addToGuestCart } from '../features/cartSlice';
import { addToWishlist, removeFromWishlist } from '../features/wishlistSlice';
import ProductRail from '../components/products/ProductRail';
import Button from '../components/common/Button';
import TrustBadges from '../components/common/TrustBadges';
import { API_ENDPOINTS, APP_NAME, APP_SLOGAN, ROUTES } from '../constants';
import { HomepageContent, Product, ResolvedProductRail } from '../types';
import { apiClient } from '../apiClient';
import { resolveImageUrl } from '../utils/image';
import { loginWithRedirect } from '../utils/navigation';
import { getRecentlyViewedIds } from '../utils/recentlyViewed';
import useDocumentTitle from '../hooks/useDocumentTitle';

// Rendered until the CMS content loads (mirrors the admin defaults)
const FALLBACK_HOMEPAGE: HomepageContent = {
  hero: {
    eyebrow: `${APP_NAME} | Korean Jewellery Edit`,
    headline: 'Luxury look.\nBudget-friendly price.',
    subheadline: `${APP_SLOGAN} Discover easy-to-style earrings, chokers, pendants and rings designed to make everyday outfits feel polished without premium-store pricing.`,
    primaryCtaLabel: 'Shop all jewellery',
    primaryCtaLink: ROUTES.PRODUCTS,
    secondaryCtaLabel: 'Shop under ₹599',
    secondaryCtaLink: `${ROUTES.PRODUCTS}?maxPrice=599`,
    infoCards: [
      {
        id: 'why-us',
        title: `Why shoppers choose ${APP_NAME}`,
        body: 'On-trend Korean designs, skin-friendly finish, and prices that fit college and daily-use budgets.',
        visible: true
      },
      {
        id: 'most-loved',
        title: 'Most loved this week',
        body: 'Lightweight statement earrings and layered pendant sets are trending right now.',
        visible: true
      }
    ]
  },
  pricePoints: [
    { id: 'under-399', label: 'Under ₹399', body: 'Premium-looking picks at a smart price point.', link: `${ROUTES.PRODUCTS}?maxPrice=399`, visible: true },
    { id: 'under-599', label: 'Under ₹599', body: 'Premium-looking picks at a smart price point.', link: `${ROUTES.PRODUCTS}?maxPrice=599`, visible: true },
    { id: 'under-999', label: 'Under ₹999', body: 'Premium-looking picks at a smart price point.', link: `${ROUTES.PRODUCTS}?maxPrice=999`, visible: true }
  ],
  collections: [
    { id: 'earrings', eyebrow: 'Collections', title: 'Earrings edit', body: 'Studs, hoops and drops for daily wear, office fits and festive looks.', link: `${ROUTES.PRODUCTS}?category=earrings`, image: null, visible: true, order: 1 },
    { id: 'pendants', eyebrow: 'Collections', title: 'Pendants & chokers', body: 'Layer-friendly pieces that elevate simple kurtis, tops and dresses.', link: `${ROUTES.PRODUCTS}?category=necklaces-pendants`, image: null, visible: true, order: 2 },
    { id: 'rings', eyebrow: 'Collections', title: 'Ring stacks', body: 'Minimal rings and statement stacks for everyday styling.', link: `${ROUTES.PRODUCTS}?category=rings`, image: null, visible: true, order: 3 }
  ]
};

const COLLECTION_GRADIENTS = [
  'linear-gradient(135deg, rgba(236, 198, 217, 0.3), rgba(255, 255, 255, 0.92))',
  'linear-gradient(135deg, rgba(210, 203, 245, 0.24), rgba(255, 255, 255, 0.96))',
  'linear-gradient(135deg, rgba(224, 208, 247, 0.24), rgba(255, 255, 255, 0.95))'
];

const HomePage: React.FC = () => {
  useDocumentTitle();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { token } = useAppSelector((state) => state.auth);
  const [content, setContent] = useState<HomepageContent>(FALLBACK_HOMEPAGE);
  const [rails, setRails] = useState<ResolvedProductRail[]>([]);
  const [recentlyViewed, setRecentlyViewed] = useState<Product[]>([]);
  const wishlistedIds = useAppSelector((state) =>
    new Set((state.wishlist.wishlist?.items || []).map((item) => item.product?._id))
  );

  useEffect(() => {
    apiClient
      .get(`${API_ENDPOINTS.SITE_SETTINGS}/homepage`)
      .then((response) => {
        if (response.data?.homepage?.hero) {
          setContent(response.data.homepage as HomepageContent);
        }
      })
      .catch(() => {});
    apiClient
      .get(`${API_ENDPOINTS.SITE_SETTINGS}/homepage-rails`)
      .then((response) => setRails((response.data.rails || []) as ResolvedProductRail[]))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const ids = getRecentlyViewedIds();
    if (ids.length === 0) return;
    apiClient
      .get(API_ENDPOINTS.PRODUCTS, { params: { ids: ids.join(','), limit: ids.length } })
      .then((response) => {
        const products = (response.data.products || []) as Product[];
        const byId = new Map(products.map((p) => [p._id, p]));
        // Preserve most-recently-viewed-first order
        setRecentlyViewed(ids.map((id) => byId.get(id)).filter(Boolean) as Product[]);
      })
      .catch(() => {});
  }, []);

  const visibleInfoCards = content.hero.infoCards.filter((c) => c.visible);
  const visiblePricePoints = content.pricePoints.filter((p) => p.visible);
  const visibleCollections = content.collections
    .filter((c) => c.visible)
    .sort((a, b) => a.order - b.order);

  const [firstRail, ...remainingRails] = rails;

  const handleAddToCart = async (product: Product) => {
    if (!token) {
      dispatch(addToGuestCart({ product, quantity: 1 }));
      return;
    }
    await dispatch(addToCart({ productId: product._id, quantity: 1 }));
  };

  const handleToggleWishlist = async (product: Product) => {
    if (!token) {
      navigate(loginWithRedirect(window.location.pathname));
      return;
    }
    if (wishlistedIds.has(product._id)) {
      await dispatch(removeFromWishlist({ productId: product._id }));
      return;
    }
    await dispatch(addToWishlist({ productId: product._id }));
  };

  return (
    <Box>
      {/* Hero — original warm design */}
      <Paper
        sx={{
          p: { xs: 2.25, sm: 3, md: 5 },
          mb: { xs: 3, md: 4 },
          mt: { xs: 0.5, sm: 1, md: 1.5 },
          background:
            'linear-gradient(140deg, rgba(236, 198, 217, 0.35) 0%, rgba(248, 244, 249, 0.96) 42%, #ffffff 100%)'
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1.1fr 0.9fr' },
            gap: { xs: 3, md: 5 },
            alignItems: 'stretch'
          }}
        >
          <Box>
            {content.hero.eyebrow && (
              <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 1.5 }}>
                {content.hero.eyebrow}
              </Typography>
            )}
            <Typography variant="h2" sx={{ mb: 1.25, fontSize: { xs: '2rem', sm: '2.2rem', md: '2.6rem' } }}>
              {content.hero.headline.split('\n').map((line, i, arr) => (
                <React.Fragment key={i}>
                  {line}
                  {i < arr.length - 1 && <br />}
                </React.Fragment>
              ))}
            </Typography>
            {content.hero.subheadline && (
              <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 560, mb: 3 }}>
                {content.hero.subheadline}
              </Typography>
            )}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              {content.hero.primaryCtaLabel && (
                <Button
                  variant="contained"
                  color="primary"
                  component={RouterLink}
                  to={content.hero.primaryCtaLink || ROUTES.PRODUCTS}
                  fullWidth
                  sx={{ width: { xs: '100%', sm: 'auto' } }}
                >
                  {content.hero.primaryCtaLabel}
                </Button>
              )}
              {content.hero.secondaryCtaLabel && (
                <Button
                  variant="outlined"
                  color="primary"
                  component={RouterLink}
                  to={content.hero.secondaryCtaLink || ROUTES.PRODUCTS}
                  fullWidth
                  sx={{ width: { xs: '100%', sm: 'auto' } }}
                >
                  {content.hero.secondaryCtaLabel}
                </Button>
              )}
            </Stack>
          </Box>

          <Stack spacing={1.5}>
            {visibleInfoCards.map((card, index) => (
              <Paper
                key={card.id}
                sx={{
                  p: 2.5,
                  backgroundColor: 'rgba(255,255,255,0.76)',
                  ...(index === 0 ? { borderColor: 'rgba(214, 90, 136, 0.2)' } : {})
                }}
              >
                <Typography variant="subtitle1" sx={{ mb: 0.75 }}>
                  {card.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {card.body}
                </Typography>
              </Paper>
            ))}
          </Stack>
        </Box>
      </Paper>

      {/* Trust badges — right after hero, before products */}
      <Box sx={{ mb: { xs: 3, md: 4 } }}>
        <TrustBadges />
      </Box>

      {/* Admin-managed product rails (first one leads, rest follow the price points) */}
      {firstRail && (
        <ProductRail
          key={firstRail.id}
          title={firstRail.title}
          subtitle={firstRail.subtitle}
          products={firstRail.products}
          viewAllHref={firstRail.viewAllLink || undefined}
          onAddToCart={handleAddToCart}
          onAddToWishlist={handleToggleWishlist}
        />
      )}

      {/* Price-point quick links */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
          gap: 1.5,
          mb: 6
        }}
      >
        {visiblePricePoints.map((item) => (
          <Box
            key={item.id}
            component={RouterLink}
            to={item.link}
            sx={{
              textDecoration: 'none',
              p: 2.2,
              borderRadius: 3,
              border: '1px solid rgba(43, 37, 43, 0.1)',
              backgroundColor: '#fff',
              color: 'text.primary',
              transition: 'all 180ms ease',
              '&:hover': {
                borderColor: 'primary.main',
                transform: 'translateY(-2px)'
              }
            }}
          >
            <Typography variant="h6">{item.label}</Typography>
            <Typography variant="body2" color="text.secondary">
              {item.body}
            </Typography>
          </Box>
        ))}
      </Box>

      {remainingRails.map((rail) => (
        <ProductRail
          key={rail.id}
          title={rail.title}
          subtitle={rail.subtitle}
          products={rail.products}
          viewAllHref={rail.viewAllLink || undefined}
          onAddToCart={handleAddToCart}
          onAddToWishlist={handleToggleWishlist}
        />
      ))}

      {/* Collection navigation (admin-managed) */}
      <Box
        sx={{
          mb: 8,
          mt: 3.5,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
          gap: 2
        }}
      >
        {visibleCollections.map((card, index) => (
          <Box
            key={card.id}
            component={RouterLink}
            to={card.link}
            sx={{
              p: 3.2,
              borderRadius: 3,
              textDecoration: 'none',
              color: 'inherit',
              position: 'relative',
              overflow: 'hidden',
              background: COLLECTION_GRADIENTS[index % COLLECTION_GRADIENTS.length],
              border: '1px solid rgba(43, 37, 43, 0.08)',
              transition: 'transform 180ms ease',
              '&:hover': { transform: 'translateY(-2px)' }
            }}
          >
            {card.image && (
              <Box
                component="img"
                src={resolveImageUrl(card.image)}
                alt={card.title}
                sx={{
                  position: 'absolute',
                  right: 0,
                  top: 0,
                  height: '100%',
                  width: '42%',
                  objectFit: 'cover',
                  opacity: 0.85,
                  maskImage: 'linear-gradient(to left, black 55%, transparent)'
                }}
              />
            )}
            <Box sx={{ position: 'relative', maxWidth: card.image ? '60%' : '100%' }}>
              {card.eyebrow && (
                <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 0.5 }}>
                  {card.eyebrow}
                </Typography>
              )}
              <Typography variant="h5" sx={{ mb: 1.2 }}>
                {card.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {card.body}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>

      {recentlyViewed.length > 0 && (
        <ProductRail
          title="Recently viewed"
          subtitle="Pick up where you left off."
          products={recentlyViewed}
          onAddToCart={handleAddToCart}
          onAddToWishlist={handleToggleWishlist}
        />
      )}
    </Box>
  );
};

export default HomePage;
